/* ============================================================
   PAINTBOX CART DRAWER — interactivity
   cart-paintbox.js

   Hooks into Dawn's existing pub/sub:
   - subscribe(PUB_SUB_EVENTS.cartUpdate, …)  — drawer just refreshed
   - publish(PUB_SUB_EVENTS.cartUpdate, …)    — broadcast our changes

   Uses event delegation at document level so handlers survive
   the drawer's HTML being replaced after each cart change.
   ============================================================ */

(function () {
  'use strict';

  /* Wait for Dawn's PUB_SUB_EVENTS to be available */
  function whenReady(cb) {
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') return cb();
    setTimeout(function () { whenReady(cb); }, 30);
  }

  /* ── Helpers ──────────────────────────────────────────────── */

  function fetchSections() {
    /* Fetch the cart-drawer section + cart-icon-bubble HTML so we can
       update them after a cart mutation, mirroring Dawn's pattern. */
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function syncEmptyState(drawer, isEmpty) {
    /* Dawn's cart.js toggles `is-empty` on <cart-drawer> and the cart footer
       whenever item_count changes — but only via its own handlers. Our
       handlers must do the same so CSS rules like
       `cart-drawer.is-empty .drawer__header { display: none }` don't keep
       the header hidden after we transition from empty → populated. */
    if (drawer) drawer.classList.toggle('is-empty', isEmpty);
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
  }

  function refreshCartDrawer(parsedCart, knownItemCount) {
    const drawer = document.querySelector('cart-drawer');

    /* Sync is-empty class. After a successful add we know item_count > 0;
       for change.js responses we get item_count back in the payload. */
    let itemCount = knownItemCount;
    if (itemCount == null && parsedCart && typeof parsedCart.item_count === 'number') {
      itemCount = parsedCart.item_count;
    }
    if (typeof itemCount === 'number') {
      syncEmptyState(drawer, itemCount === 0);
    }

    if (drawer && typeof drawer.renderContents === 'function' && parsedCart && parsedCart.sections) {
      drawer.renderContents(parsedCart);
    } else {
      /* Fallback: full reload of the drawer HTML by re-fetching */
      fetch('/?sections=' + fetchSections().join(','))
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          if (drawer && sections['cart-drawer']) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
            const newDrawer = doc.querySelector('cart-drawer');
            if (newDrawer) {
              drawer.innerHTML = newDrawer.innerHTML;
              /* Mirror class state from the freshly-rendered server output */
              if (newDrawer.classList.contains('is-empty')) {
                syncEmptyState(drawer, true);
              } else {
                syncEmptyState(drawer, false);
              }
            }
          }
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble && sections['cart-icon-bubble']) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
            const newBubble = doc.getElementById('cart-icon-bubble');
            if (newBubble) bubble.innerHTML = newBubble.innerHTML;
          }
        });
    }
  }

  function setBtnState(btn, state, originalLabel) {
    if (state === 'loading') {
      btn.disabled = true;
      btn.dataset.originalLabel = btn.textContent;
      btn.textContent = 'Adding\u2026';
    } else if (state === 'added') {
      btn.disabled = true;
      btn.classList.add('is-added');
      btn.textContent = 'Added';
    } else if (state === 'error') {
      btn.disabled = false;
      btn.textContent = btn.dataset.originalLabel || 'Add';
      btn.classList.add('has-error');
      setTimeout(function () { btn.classList.remove('has-error'); }, 1500);
    }
  }

  /* ── Add-on quick-add ─────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.pbx-addon__btn');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    setBtnState(btn, 'loading');

    const sections = fetchSections();
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
      body: formData
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          setBtnState(btn, 'error');
          return;
        }
        setBtnState(btn, 'added');

        /* Capture the rewards-bar fill BEFORE the drawer DOM is replaced
           so we can animate from old → new. */
        captureCurrentRewards();

        /* Refresh the drawer + cart-icon bubble. Dawn's cart-drawer doesn't
           subscribe to cartUpdate to re-render itself — the calling code
           is expected to invoke renderContents() explicitly (see how
           product-form.js does this.cart.renderContents(response)).
           After a successful add, item_count is guaranteed > 0, so we
           pass 1 as a hint to flip is-empty off. */
        refreshCartDrawer(data, 1);

        /* Also publish for any other components that listen
           (cart-icon-bubble, header bubble counters, etc.). */
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'pbx-quick-add',
            productVariantId: variantId,
            cartData: data
          });
        }
      })
      .catch(function () {
        setBtnState(btn, 'error');
      });
  });

  /* ── Subscribe & save inline upsell ───────────────────────── */

  document.addEventListener('click', function (e) {
    const link = e.target.closest('.pbx-subscribe-upsell');
    if (!link) return;
    e.preventDefault();
    if (link.disabled) return;

    const lineKey = link.dataset.lineKey;
    const sellingPlan = link.dataset.sellingPlan;
    const lineIndex = link.dataset.lineIndex;
    if (!lineKey || !sellingPlan) return;

    link.disabled = true;
    const originalText = link.textContent;
    link.textContent = 'Updating\u2026';

    const sections = fetchSections();
    const body = JSON.stringify({
      id: lineKey,
      quantity: parseInt(link.dataset.quantity || '1', 10),
      selling_plan: sellingPlan,
      sections: sections.join(','),
      sections_url: window.location.pathname
    });

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          link.textContent = originalText;
          link.disabled = false;
          return;
        }

        captureCurrentRewards();
        refreshCartDrawer(data);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'pbx-subscribe-upsell',
            cartData: data
          });
        }
      })
      .catch(function () {
        link.textContent = originalText;
        link.disabled = false;
      });
  });

  /* ── Rewards ladder: animate fill on cart update ──────────── */

  /* Stash the previous total in memory so we can animate from old to new
     after the drawer DOM is replaced. The new HTML is already at the new
     width \u2014 we briefly snap it to the OLD width, force reflow, then let
     the CSS transition take it to the new width. */
  let lastTotalCents = null;
  let lastFillPct = null;

  function captureCurrentRewards() {
    const fill = document.querySelector('.pbx-rewards__fill');
    if (!fill) return;
    const w = fill.style.width || getComputedStyle(fill).width;
    lastFillPct = w;
  }

  function animateRewardsFromPrevious() {
    const fill = document.querySelector('.pbx-rewards__fill');
    if (!fill || lastFillPct == null) return;

    const targetWidth = fill.style.width || fill.dataset.targetWidth;
    /* snap to old */
    fill.style.transition = 'none';
    fill.style.width = lastFillPct;
    /* force reflow */
    void fill.offsetWidth;
    /* re-enable transition and animate to new */
    fill.style.transition = '';
    fill.style.width = targetWidth;
  }

  /* ── Shrink cancellation / subscription terms text ─────────── */

  /* Shopify's <shopify-payment-terms> is a web component that renders the
     cancellation / subscription agreement text. Its default styling often
     ignores our CSS, so we force the text inline and re-apply after every
     drawer render. */
  function crushPaymentTerms() {
    var terms = document.querySelectorAll(
      'cart-drawer shopify-payment-terms, ' +
      '#dynamic-checkout-cart shopify-payment-terms, ' +
      '.cart-drawer .shopify-cleanslate, ' +
      '#dynamic-checkout-cart .shopify-cleanslate'
    );
    if (!terms.length) return;

    for (var i = 0; i < terms.length; i++) {
      var el = terms[i];
      setTiny(el);
      var all = el.querySelectorAll('*');
      for (var j = 0; j < all.length; j++) setTiny(all[j]);
      if (el.shadowRoot) {
        var shadowAll = el.shadowRoot.querySelectorAll('*');
        for (var k = 0; k < shadowAll.length; k++) setTiny(shadowAll[k]);
      }
    }
  }

  function setTiny(el) {
    if (!el || !el.style) return;
    /* For light-DOM / slotted text */
    el.style.setProperty('font-size', '0.55rem', 'important');
    el.style.setProperty('line-height', '1.4', 'important');
    el.style.setProperty('letter-spacing', '0', 'important');
    el.style.setProperty('margin', '0', 'important');
    el.style.setProperty('padding', '0', 'important');

    /* For Shopify's shadow-DOM <shopify-payment-terms>, zoom the whole host.
       zoom reflows in WebKit/Blink; transform is the fallback for browsers
       that don't support zoom. */
    if (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('zoom', '0.85')) {
      el.style.zoom = '0.85';
    } else {
      el.style.transform = 'scale(0.85)';
      el.style.transformOrigin = 'top left';
    }
    el.style.opacity = '0.7';
  }

  function watchPaymentTerms() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var observer = new MutationObserver(function () {
      crushPaymentTerms();
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }

  /* ── Wire pub/sub ────────────────────────────────────────── */

  whenReady(function () {
    /* Shrink payment-terms text now and on every drawer render */
    crushPaymentTerms();
    watchPaymentTerms();
    /* Shopify's web component can be injected a bit later, so retry */
    setTimeout(crushPaymentTerms, 500);
    setTimeout(crushPaymentTerms, 1200);

    /* Capture rewards state BEFORE Dawn's renderContents runs.
       Dawn's cart.js publishes 'cart-update' AFTER replacing HTML, so we
       can't intercept pre-replace cleanly. Instead, we capture on every
       click that might cause a cart change. */
    document.addEventListener('click', function (e) {
      const target = e.target.closest(
        '.pbx-addon__btn, .pbx-subscribe-upsell, ' +
        '.cart-remove-button, .quantity__button, ' +
        'cart-remove-button button'
      );
      if (target) captureCurrentRewards();
    }, true);

    /* Also capture before qty input changes (debounced upstream) */
    document.addEventListener('change', function (e) {
      if (e.target && e.target.classList && e.target.classList.contains('quantity__input')) {
        captureCurrentRewards();
      }
    });

    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      /* Drawer was just re-rendered with new totals; animate the bar */
      requestAnimationFrame(animateRewardsFromPrevious);
      requestAnimationFrame(crushPaymentTerms);
      /* The disclaimer is rendered async by Shopify's JS; retry */
      setTimeout(crushPaymentTerms, 500);
      setTimeout(crushPaymentTerms, 1200);
    });
  });
})();
